<script setup lang="ts">
import { Ref, ref, toRefs, inject, computed } from "vue";
import { titleInputRules } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import FileDTO = App.Data.FileDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;
import { useBaseStore } from "@/stores/base";
import { useApiClient } from "@/api/client";
import useToast from "@/ui/composables/useToast";
import useDialog from "@/ui/composables/useDialog";
import TagMenu from "@/ui/components/FileBrowser/TagMenu.vue";
import TagChip from "@/ui/components/FileBrowser/TagChip.vue";
import FileTagDTO = App.Data.FileTagDTO;
import DashboardMode = App.Enums.DashboardMode;

const { t } = useI18n();

type CreateFileAction = (file: FileDTO) => Promise<any>;
const createFileAction = inject<CreateFileAction>("createFileAction");
type UpdateFilePropertiesAction = (file: FileDTO) => Promise<any>;
const updateFilePropertiesAction = inject<UpdateFilePropertiesAction>(
	"updateFilePropertiesAction"
);

const isDialogVisible = defineModel<boolean>();

/** Save happened - the listing behind this dialog is now out of date. */
const emit = defineEmits<{ saved: [] }>();

const props = defineProps<{
	nodes: ResourceTreeNodeDO[];
	fileToEdit: FileDTO;
	resourceTreeCategory: "requested_documents" | "dashboards";
}>();

const isSharedWithMe = computed(() => {
	const ownerId = props.fileToEdit?.user?.id;

	return !!ownerId && ownerId !== useBaseStore().currentUser?.id;
});

const { nodes, fileToEdit } = toRefs(props);
const isFormValid: Ref<boolean> = ref(false);

/* ------------------------------------------------------------------ *
 * Mode - dashboards only
 * ------------------------------------------------------------------ */

/**
 * Asked here rather than in a dialog of its own after Save: the mode is a
 * property of the dashboard, so it belongs on the form that collects its
 * properties. `SelectDashboardMode` still handles changing it from inside an
 * open dashboard, where the longer explanation has room.
 */
const isDashboard = computed(() => props.resourceTreeCategory === "dashboards");

const MODES: { value: DashboardMode; icon: string }[] = [
	{ value: "web", icon: "fa: fa-regular fa-desktop" },
	{ value: "print", icon: "fa: fa-regular fa-print" },
];

/** A dashboard always has one; the toggle must never sit on nothing. */
const mode = computed<DashboardMode>({
	get: () => fileToEdit.value?.mode ?? "web",
	set: (value) => {
		fileToEdit.value.mode = value;
	},
});

const filteredNodes = computed(() => {
	return nodes.value?.filter((node) => node.parent_id !== null) ?? [];
});

/* ------------------------------------------------------------------ *
 * Tags
 * ------------------------------------------------------------------ */

type DraftTag = {
	id: number | null;
	name: string;
	rgb_color_value: string | null;
	files_count: number | null;
};

const toDraftTag = (tag: FileTagDTO): DraftTag => ({
	id: tag.id,
	name: tag.name,
	rgb_color_value: tag.rgb_color_value,
	files_count: tag.files_count ?? null,
});

const availableTags = ref<DraftTag[]>([]);
const isLoadingTags = ref(false);
const selectedTags = ref<DraftTag[]>(
	(fileToEdit.value?.tags ?? []).map(toDraftTag)
);

/** Names and colours as loaded, so Save only patches what actually changed. */
const loadedTags = ref(new Map<number, DraftTag>());

const loadTags = async (): Promise<void> => {
	isLoadingTags.value = true;

	const response = await useApiClient("fileTags").getFileTags();

	if (response.isOk()) {
		availableTags.value = response.value.map(toDraftTag);
		loadedTags.value = new Map(
			response.value.map((tag) => [tag.id, toDraftTag(tag)])
		);

		// The field matches its selection to its items by identity, so the tags
		// already on the file have to become the loaded instances.
		selectedTags.value = selectedTags.value.map(
			(selected) =>
				availableTags.value.find((tag) => tag.id === selected.id) ?? selected
		);
	}

	isLoadingTags.value = false;
};

const onTagsChanged = (values: DraftTag[]): void => {
	selectedTags.value = values;
};

/** Every name the New tag button may not reuse. */
const takenTagNames = computed(() => [
	...availableTags.value.map((tag) => tag.name),
	...selectedTags.value.map((tag) => tag.name),
]);

const isNameTaken = (name: string): boolean =>
	takenTagNames.value.some(
		(taken) => taken.toLowerCase() === name.toLowerCase()
	);

/**
 * A new tag joins the selection as a draft; Save is what turns it into a real
 * tag, so backing out of the dialog leaves the tag list untouched.
 */
const addDraftTag = (name: string, rgbColorValue: string | null): void => {
	if (isNameTaken(name)) {
		return;
	}

	selectedTags.value = [
		...selectedTags.value,
		{ id: null, name, rgb_color_value: rgbColorValue, files_count: 0 },
	];
};

const updateTag = (
	tag: DraftTag,
	name: string,
	rgbColorValue: string | null
): void => {
	tag.name = name;
	tag.rgb_color_value = rgbColorValue;
};

/**
 * Drops the tag from this dialog's state, wherever it is referenced. Matched by
 * id where there is one: the selection and the item list normally share
 * instances, but nothing enforces that.
 */
const forgetTag = (tag: DraftTag): void => {
	const isTag = (each: DraftTag): boolean =>
		tag.id === null ? each === tag : each.id === tag.id;

	selectedTags.value = selectedTags.value.filter((each) => !isTag(each));
	availableTags.value = availableTags.value.filter((each) => !isTag(each));

	if (tag.id !== null) {
		loadedTags.value.delete(tag.id);
	}
};

/**
 * Deleting is immediate rather than deferred to Save, unlike renames: the tag
 * is shared, so it leaves every file carrying it, and the confirmation says how
 * many that is. A tag that was never saved is only dropped from the draft.
 */
const deleteTag = async (tag: DraftTag): Promise<void> => {
	if (tag.id === null) {
		forgetTag(tag);

		return;
	}

	const confirmed = await useDialog().confirm({
		title: t("fileBrowser.tags.deleteTitle"),
		message:
			tag.files_count !== null && tag.files_count > 0
				? t("fileBrowser.tags.deleteUsedMessage", tag.files_count, {
						named: { name: tag.name, count: tag.files_count },
				  })
				: t("fileBrowser.tags.deleteMessage", { name: tag.name }),
		isDelete: true,
	});

	if (!confirmed) {
		return;
	}

	const response = await useApiClient("fileTags").deleteFileTag(tag.id);

	if (!response.isOk()) {
		useToast().error(
			response.error?.message ||
				t("fileBrowser.tags.deleteFailed", { name: tag.name })
		);

		return;
	}

	forgetTag(tag);
};

const hasTagChanged = (tag: DraftTag): boolean => {
	const loaded = tag.id == null ? null : loadedTags.value.get(tag.id);

	return (
		!!loaded &&
		(loaded.name !== tag.name || loaded.rgb_color_value !== tag.rgb_color_value)
	);
};

/**
 * Tag edits ride along with Save, like every other field in this dialog: a
 * drafted tag is created, a renamed or recoloured one is patched, and Cancel
 * leaves the tag list untouched. Returns null when a call failed, so the
 * dialog can stay open with the user's input intact.
 */
const persistTags = async (): Promise<number[] | null> => {
	const client = useApiClient("fileTags");
	const tagIds: number[] = [];

	for (const tag of selectedTags.value) {
		// `== null` on purpose: an id-less tag has to be created, and treating a
		// missing one as existing would push `undefined` into `tag_ids` and drop
		// the tag silently.
		if (tag.id == null) {
			const response = await client.createFileTag(
				tag.name,
				tag.rgb_color_value
			);

			if (!response.isOk()) {
				useToast().error(
					response.error?.message ||
						t("fileBrowser.tags.createFailed", { name: tag.name })
				);

				return null;
			}

			tag.id = response.value.id;
			tag.rgb_color_value = response.value.rgb_color_value;
			availableTags.value = [...availableTags.value, tag];
			loadedTags.value.set(tag.id, toDraftTag(response.value));
			tagIds.push(tag.id);

			continue;
		}

		if (hasTagChanged(tag)) {
			const response = await client.updateFileTag(tag.id, {
				name: tag.name,
				rgb_color_value: tag.rgb_color_value ?? undefined,
			});

			if (!response.isOk()) {
				useToast().error(
					response.error?.message ||
						t("fileBrowser.tags.updateFailed", { name: tag.name })
				);

				return null;
			}

			loadedTags.value.set(tag.id, toDraftTag(response.value));
		}

		tagIds.push(tag.id);
	}

	return tagIds;
};

loadTags();

const closeModal = () => {
	isDialogVisible.value = false;
};

/** The tag selection travels as ids, without touching the file passed in. */
const fileWithTags = (tagIds: number[]) => ({
	...fileToEdit.value,
	tag_ids: tagIds,
});

const createFile = async (tagIds: number[]) => {
	return await createFileAction(fileWithTags(tagIds));
};

const updateFile = async (tagIds: number[]) => {
	return await updateFilePropertiesAction(fileWithTags(tagIds));
};

const isSaving = ref(false);

const saveStoredDocument = async () => {
	isSaving.value = true;

	const tagIds = await persistTags();

	if (tagIds === null) {
		isSaving.value = false;

		return;
	}

	if (!fileToEdit.value?.id) {
		await createFile(tagIds);
	} else {
		await updateFile(tagIds);
	}

	isSaving.value = false;
	// Emitted even when a call failed: reloading then shows what the server
	// actually holds, which is what the user needs to see either way.
	emit("saved");
	closeModal();
};
</script>

<template>
	<v-dialog
		v-model="isDialogVisible"
		scrim="#000000"
		overlay-color="black"
		max-width="50vw"
	>
		<v-form v-model="isFormValid">
			<v-card>
				<v-toolbar>
					<template v-slot:prepend>
						<v-icon start class="ml-3 mr-5">mdi-file-plus-outline</v-icon>
						<v-toolbar-title class="pt-1">
							{{
								!fileToEdit?.id
									? t("fileBrowser.fileDialog.createTitle")
									: t("fileBrowser.fileDialog.updateTitle")
							}}
						</v-toolbar-title>
					</template>
				</v-toolbar>
				<v-divider />
				<v-row>
					<v-col cols="12">
						<v-card-text class="d-flex flex-column ga-2">
							<v-select
								v-if="!isSharedWithMe"
								v-model="fileToEdit.resource_tree_node_id"
								:items="filteredNodes"
								item-title="name"
								item-value="id"
								:label="t('fileBrowser.fileDialog.parentFolder')"
								hide-details
							/>
							<v-text-field
								v-model="fileToEdit.title"
								:rules="titleInputRules(t)"
								:label="t('fileBrowser.fileDialog.name')"
								clearable
								hide-details
							/>
							<v-textarea
								v-model="fileToEdit.description"
								:label="t('fileBrowser.fileDialog.description')"
								hide-details
							/>

							<div v-if="isDashboard && !fileToEdit?.id" class="mode-field">
								<span class="mode-field__label">
									{{ t("fileBrowser.fileDialog.mode") }}
								</span>
								<v-btn-toggle
									v-model="mode"
									mandatory
									density="compact"
									variant="outlined"
									divided
								>
									<v-btn
										v-for="option in MODES"
										:key="option.value"
										:value="option.value"
										:prepend-icon="option.icon"
										:text="t(`fileBrowser.modes.${option.value}`)"
										size="small"
									/>
								</v-btn-toggle>
								<span class="mode-field__hint">{{
									t(`fileBrowser.modeHints.${mode}`)
								}}</span>
							</div>
							<!-- Applying and creating are kept apart: the field only picks
							     from existing tags, the button makes new ones. -->
							<div class="d-flex align-start ga-2">
								<v-autocomplete
									:model-value="selectedTags"
									:items="availableTags"
									:loading="isLoadingTags"
									item-title="name"
									return-object
									:label="t('fileBrowser.tags.label')"
									class="flex-grow-1"
									hide-details
									chips
									closable-chips
									multiple
									@update:model-value="onTagsChanged"
								>
									<template #chip="{ props: chipProps, item }">
										<tag-menu
											:name="item.raw.name"
											:rgb-color-value="item.raw.rgb_color_value"
											:taken-names="takenTagNames"
											is-deletable
											@submit="
												(name, color) => updateTag(item.raw, name, color)
											"
											@delete="deleteTag(item.raw)"
										>
											<template #activator="{ props: activatorProps }">
												<tag-chip
													v-bind="{ ...chipProps, ...activatorProps }"
													:name="item.raw.name"
													:rgb-color-value="item.raw.rgb_color_value"
													class="tag-chip--editable"
													role="button"
													:aria-label="
														t('fileBrowser.tags.editAriaLabel', {
															name: item.raw.name,
														})
													"
												/>
											</template>
										</tag-menu>
									</template>

									<template #item="{ props: itemProps, item }">
										<v-list-item v-bind="itemProps">
											<template #title>
												<tag-chip
													:name="item.raw.name"
													:rgb-color-value="item.raw.rgb_color_value"
												/>
											</template>
										</v-list-item>
									</template>

									<template #no-data>
										<v-list-item>
											<v-list-item-subtitle>
												{{
													availableTags.length
														? t("fileBrowser.tags.noMatch")
														: t("fileBrowser.tags.none")
												}}
											</v-list-item-subtitle>
										</v-list-item>
									</template>
								</v-autocomplete>

								<tag-menu
									:taken-names="takenTagNames"
									:submit-label="t('fileBrowser.tags.create')"
									@submit="addDraftTag"
								>
									<template #activator="{ props: activatorProps }">
										<v-btn
											v-bind="activatorProps"
											prepend-icon="mdi-plus"
											:text="t('fileBrowser.tags.newTag')"
											height="48"
										/>
									</template>
								</tag-menu>
							</div>
						</v-card-text>
					</v-col>
				</v-row>

				<v-divider style="border-color: unset !important"></v-divider>

				<v-card-actions class="ma-2">
					<v-spacer></v-spacer>
					<v-btn
						:text="t('fileBrowser.actions.cancel')"
						variant="text"
						color="error"
						@click="closeModal"
					/>
					<v-btn
						append-icon="mdi-content-save"
						color="primary"
						:text="t('fileBrowser.actions.save')"
						variant="flat"
						:loading="isSaving"
						:disabled="isFormValid === false"
						@click="saveStoredDocument()"
					/>
				</v-card-actions>
			</v-card>
		</v-form>
	</v-dialog>
</template>

<style lang="scss" scoped>
.mode-field {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	padding: 4px 0;

	&__label {
		font-size: 0.875rem;
		opacity: 0.7;
	}

	&__hint {
		flex: 1 1 220px;
		min-width: 0;
		font-size: 0.75rem;
		opacity: 0.7;
	}
}

.tag-chip--editable {
	cursor: pointer;
}
</style>
